import { fn } from '@ember/helper';
import { gt } from 'tio-ui/utilities';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedFunction } from 'reactiveweb/function';
import Component from '@glimmer/component';
import didIntersect from 'ember-scroll-modifiers/modifiers/did-intersect';
import safeFormatDate from '../../helpers/safe-format-date.ts';
import safeFormatTime from '../../helpers/safe-format-time.ts';
import Table from 'tio-ui/components/table';
import Paginator from 'tio-ui/components/paginator';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { Store } from '@warp-drive/core';
import type { NotificationHistoryExtension as NotificationHistoryModel } from 'tio-data/data/resources/notification-history.ext';
import { query } from '@warp-drive/utilities/json-api';

interface NotificationHistoriesTableSignature {
  Args: {
    employee: EmployeeModel;
    noteSubtypeFilter: string;
    limit: number;
  };
}

export default class NotificationHistoriesTableComponent extends Component<NotificationHistoriesTableSignature> {
  @service declare store: Store;
  @tracked page = 1;
  @tracked isInViewport = false;

  get isLoading() {
    return this.notificationHistoriesData.isLoading;
  }

  notificationHistoriesData = trackedFunction(this, async () => {
    if (!this.args.employee) {
      return { data: [] as NotificationHistoryModel[], meta: undefined };
    }
    const limit = this.args.limit || 10;
    const offset = (this.page - 1) * limit;

    const params: Record<string, string | number | boolean> = {
      'filter[employee]': this.args.employee.id as string,
      'page[limit]': limit,
      'page[offset]': offset,
      sort: '-created_at',
    };

    if (this.args.noteSubtypeFilter) {
      params['filter[noteSubtype]'] = this.args.noteSubtypeFilter;
    }

    const { content } = (await this.store.request(
      query<NotificationHistoryModel>('notification-history', params)
    )) as {
      content: { data: NotificationHistoryModel[]; meta?: { page?: { total?: number } } };
    };
    return { data: content.data, meta: content.meta };
  });

  get notificationHistories() {
    return this.notificationHistoriesData?.value?.data || [];
  }

  previousPage = () => {
    this.page = Math.max(1, this.page - 1);
  };

  nextPage = () => {
    ++this.page;
  };

  changePaginatorPage = (page: number) => {
    this.page = page;
  };

  get totalItems() {
    return this.notificationHistoriesData?.value?.meta?.page?.total ?? 0;
  }

  get totalPages() {
    if (this.totalItems) {
      return Math.ceil(this.totalItems / this.args.limit);
    }
    return 0;
  }

  <template>
    <span {{didIntersect onEnter=(fn (mut this.isInViewport) true)}} />
    {{#if this.isInViewport}}
      <Table @isLoading={{this.isLoading}}>
        <:thead as |options|>
          <options.tr>
            <options.th class="w-48">{{t
                "common.profile.notification_histories.sent_on"
              }}</options.th>
            <options.th>{{t "common.profile.notification_histories.email_type"}}</options.th>
            <options.th>{{t "common.profile.notification_histories.sent_to"}}</options.th>
          </options.tr>
        </:thead>
        <:tbody as |options|>
          {{#each this.notificationHistories as |history|}}
            <options.tr>
              <options.td>
                {{safeFormatDate history.createdAt month="short" day="2-digit" year="numeric"}}
                {{safeFormatTime
                  history.createdAt
                  hour="numeric"
                  minute="numeric"
                  second="numeric"
                }}
              </options.td>
              <options.td>{{history.noteSubtype}}</options.td>
              <options.td>{{history.toEmail}}</options.td>
            </options.tr>
          {{else}}
            <options.tr>
              <options.td colspan="3">
                {{t "common.profile.notification_histories.no_communications"}}
              </options.td>
            </options.tr>
          {{/each}}
        </:tbody>
        <:tfoot>
          <tr>
            <td class="text-right" colspan="9">
              {{#if (gt this.totalPages 1)}}
                <Paginator
                  @simple={{true}}
                  @currentPage={{this.page}}
                  @totalItems={{this.totalItems}}
                  @pageSize={{@limit}}
                  @onChange={{this.changePaginatorPage}}
                />
              {{/if}}
            </td>
          </tr>
        </:tfoot>
      </Table>
    {{/if}}
  </template>
}
